/**
 * sortOrgMembers.ts
 * V65: 조직관리 3페이지 공용 정렬 유틸리티
 *
 * 정렬 우선순위 (SSoT):
 * 1순위: 직책순 (높은 사람 먼저) - jobTitle order 오름차순 (낮을수록 높은 직책: 대표=1)
 * 2순위: 직위순 (높은 사람 먼저) - positionLevel rankWeight 내림차순 (높을수록 높은 직위: 대표=8)
 * 3순위: 최초입사일순 (오래 근무한 사람 먼저) - firstHireDate 오름차순
 * 4순위: 한글명 가나다순 - nameKo 오름차순
 *
 * null/undefined 처리:
 * - order/rankWeight 없음 → +Infinity (항상 아래)
 * - firstHireDate 없음 → +Infinity (항상 아래)
 * - nameKo 없음 → 빈 문자열 처리
 */

// ============================================
// 타입 정의
// ============================================

/**
 * 정렬에 필요한 최소 필드 (각 페이지 row 데이터에서 추출)
 *
 * 각 페이지별 매핑:
 * - OrgMembersPage: titleId, positionId, firstHireDate, nameKo
 * - OrgWorkStatusPage: jobTitle(이름), positionName(이름), name
 * - OrgLeaveManagementPage: nameKo
 */
export interface OrgMemberSortFields {
  // 직책 정렬용 (둘 중 하나)
  titleId?: string;        // ID 기반 (OrgMembersPage - Employee 타입)
  jobTitle?: string;       // 이름 기반 (OrgWorkStatusPage - EmployeeMonthlyStats 타입)

  // 직위 정렬용 (둘 중 하나)
  positionId?: string;     // ID 기반 (OrgMembersPage - Employee 타입)
  positionName?: string;   // 이름 기반 (OrgWorkStatusPage - EmployeeMonthlyStats 타입)

  // 입사일 정렬용
  firstHireDate?: string;

  // 이름 정렬용
  nameKo?: string;
  name?: string;
}

/**
 * 정렬에 필요한 마스터 데이터 맵
 *
 * 페이지에서 useJobTitles, usePositionLevelMap 등으로 조회 후 변환하여 전달
 */
export interface OrgMemberSortMaps {
  // ID → order/rankWeight (OrgMembersPage용)
  titleIdToOrderMap?: Record<string, number>;
  positionIdToRankWeightMap?: Record<string, number>;

  // 이름 → order/rankWeight (OrgWorkStatusPage용)
  titleNameToOrderMap?: Record<string, number>;
  positionNameToRankWeightMap?: Record<string, number>;
}

// ============================================
// 헬퍼 함수
// ============================================

/**
 * 직책 order 결정 (ID 우선, 이름 fallback)
 *
 * @returns order 값 (낮을수록 높은 직책, 없으면 +Infinity)
 */
function resolveJobOrder(row: OrgMemberSortFields, maps: OrgMemberSortMaps): number {
  // 1) titleId 기반 조회 (OrgMembersPage)
  if (row.titleId && maps.titleIdToOrderMap) {
    const order = maps.titleIdToOrderMap[row.titleId];
    if (typeof order === 'number' && Number.isFinite(order)) {
      return order;
    }
  }

  // 2) jobTitle 이름 기반 조회 (OrgWorkStatusPage)
  if (row.jobTitle && maps.titleNameToOrderMap) {
    const order = maps.titleNameToOrderMap[row.jobTitle];
    if (typeof order === 'number' && Number.isFinite(order)) {
      return order;
    }
  }

  // 없으면 맨 아래
  return Number.POSITIVE_INFINITY;
}

/**
 * 직위 rankWeight 결정 (ID 우선, 이름 fallback)
 *
 * @returns rankWeight 값 (높을수록 높은 직위, 없으면 0)
 */
function resolvePositionRank(row: OrgMemberSortFields, maps: OrgMemberSortMaps): number {
  // 1) positionId 기반 조회 (OrgMembersPage)
  if (row.positionId && maps.positionIdToRankWeightMap) {
    const rankWeight = maps.positionIdToRankWeightMap[row.positionId];
    if (typeof rankWeight === 'number' && Number.isFinite(rankWeight)) {
      return rankWeight;
    }
  }

  // 2) positionName 이름 기반 조회 (OrgWorkStatusPage)
  if (row.positionName && maps.positionNameToRankWeightMap) {
    const rankWeight = maps.positionNameToRankWeightMap[row.positionName];
    if (typeof rankWeight === 'number' && Number.isFinite(rankWeight)) {
      return rankWeight;
    }
  }

  // 없으면 0 (가장 낮은 직위로 처리)
  return 0;
}

/**
 * 날짜 문자열을 timestamp로 변환
 *
 * @param dateStr YYYY-MM-DD 형식
 * @returns timestamp (없거나 파싱 실패 시 +Infinity)
 */
function parseDateToNumber(dateStr: string | undefined | null): number {
  if (!dateStr) return Number.POSITIVE_INFINITY;

  // YYYY-MM-DD 형식 직접 파싱
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr).getTime();
  }

  // 그 외 형식 파싱 시도
  const ts = new Date(dateStr).getTime();
  return Number.isNaN(ts) ? Number.POSITIVE_INFINITY : ts;
}

/**
 * 이름 추출 (nameKo 우선, name fallback)
 */
function resolveName(row: OrgMemberSortFields): string {
  return row.nameKo ?? row.name ?? '';
}

// ============================================
// 메인 compare 함수
// ============================================

/**
 * 조직원 정렬 compare 함수
 *
 * 정렬 우선순위:
 * 1순위: 직책순 (높은 사람 먼저) - order 오름차순
 * 2순위: 직위순 (높은 사람 먼저) - rankWeight 내림차순
 * 3순위: 최초입사일순 (오래 근무한 사람 먼저) - 오름차순
 * 4순위: 한글명 가나다순 - 오름차순
 *
 * @example
 * ```ts
 * const sorted = [...employees].sort((a, b) =>
 *   compareOrgMember(a, b, sortMaps)
 * );
 * ```
 */
export function compareOrgMember(
  a: OrgMemberSortFields,
  b: OrgMemberSortFields,
  maps: OrgMemberSortMaps
): number {
  // 1순위: 직책순 (order 오름차순 - 낮을수록 높은 직책)
  const aJobOrder = resolveJobOrder(a, maps);
  const bJobOrder = resolveJobOrder(b, maps);
  if (aJobOrder !== bJobOrder) {
    return aJobOrder - bJobOrder;
  }

  // 2순위: 직위순 (rankWeight 내림차순 - 높을수록 높은 직위)
  const aRankWeight = resolvePositionRank(a, maps);
  const bRankWeight = resolvePositionRank(b, maps);
  if (aRankWeight !== bRankWeight) {
    return bRankWeight - aRankWeight;
  }

  // 3순위: 입사일순 (오름차순 - 오래된 순)
  const aDate = parseDateToNumber(a.firstHireDate);
  const bDate = parseDateToNumber(b.firstHireDate);
  if (aDate !== bDate) {
    return aDate - bDate;
  }

  // 4순위: 이름 가나다순 (오름차순)
  const aName = resolveName(a);
  const bName = resolveName(b);
  return aName.localeCompare(bName, 'ko');
}

// ============================================
// 맵 빌더 헬퍼
// ============================================

/**
 * JobTitle 배열에서 ID→order, name→order 맵 생성
 *
 * @param jobTitles useJobTitles() 결과
 * @returns { titleIdToOrderMap, titleNameToOrderMap }
 */
export function buildJobTitleMaps(
  jobTitles: Array<{ id: string; name: string; order: number }>
): Pick<OrgMemberSortMaps, 'titleIdToOrderMap' | 'titleNameToOrderMap'> {
  const titleIdToOrderMap: Record<string, number> = {};
  const titleNameToOrderMap: Record<string, number> = {};

  jobTitles.forEach((jt) => {
    if (jt.id) titleIdToOrderMap[jt.id] = jt.order ?? 999;
    if (jt.name) titleNameToOrderMap[jt.name] = jt.order ?? 999;
  });

  return { titleIdToOrderMap, titleNameToOrderMap };
}

/**
 * PositionLevel 배열에서 ID→rankWeight, name→rankWeight 맵 생성
 *
 * @param positionLevels usePositionLevels() 결과
 * @returns { positionIdToRankWeightMap, positionNameToRankWeightMap }
 */
export function buildPositionLevelMaps(
  positionLevels: Array<{ id: string; name: string; rankWeight: number }>
): Pick<OrgMemberSortMaps, 'positionIdToRankWeightMap' | 'positionNameToRankWeightMap'> {
  const positionIdToRankWeightMap: Record<string, number> = {};
  const positionNameToRankWeightMap: Record<string, number> = {};

  positionLevels.forEach((pl) => {
    if (pl.id) positionIdToRankWeightMap[pl.id] = pl.rankWeight ?? 0;
    if (pl.name) positionNameToRankWeightMap[pl.name] = pl.rankWeight ?? 0;
  });

  return { positionIdToRankWeightMap, positionNameToRankWeightMap };
}
